import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useOutletContext } from "react-router-dom";
import { Tooltip, Button, Modal, Divider, message, Input, Flex, Form, Upload } from "antd";
import { QuestionCircleOutlined, CloseOutlined, LoadingOutlined, PlusSquareFilled } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { format, findDeep, getProductManageDetail, changeSize } from "@/utils";
import api from "@/api";
import { productNameReg } from "@/utils/constant";
import { publicPath } from "@/utils/base";
const { confirm } = Modal;
import dataJson from "@/../public/data.json";

const prefix = "productDetail-info";
import "./index.less";
import { RcFile, UploadChangeParam, UploadFile, UploadProps } from "antd/es/upload";

function Info() {
  const {
    product_detail,
    confirmText,
    cancelText,
    release,
    delete_text,
    save_text,
    product_manage,
    save_success_text,
    staging,
    name_label,
    asterisk,
  } = dataJson;
  const [baseForm] = Form.useForm();
  const [iconLoading, setIconLoading] = useState(false);
  const [iconImageUrl, setIconImageUrl] = useState("");
  const [productManageMapper, setProductManageMapper] = useState<any>();
  const [saveLoading, setSaveLoading] = useState(false);
  const token = useRef("");

  const addFormJson = product_manage.add_form;

  const navigate = useNavigate();
  const { id } = useParams();
  const { isTampered = false, setIsTampered } = useOutletContext<{
    isTampered?: boolean;
    setIsTampered?: (value: boolean) => void;
  }>() || {};

  const productManageDetail = useSelector((state: any) => state.productManageDetail);
  const isCurrentDetail =
    Boolean(productManageDetail) && String(productManageDetail.id) === String(id);

  const handleDelete = () => {
    confirm({
      title: delete_text,
      icon: null,
      width: 328,
      centered: true,
      closable: true,
      closeIcon: <CloseOutlined />,
      okText: confirmText,
      cancelText: cancelText,
      content: (
        <div className="delete-modal-content">
          {product_detail.app_delete_content1}&quot;{productManageMapper?.name}&quot;
          {product_detail.app_delete_content2}
        </div>
      ),
      onOk: async () => {
        try {
          const res: any = await api.deleteProductManage({ ids: [id] });

          if (res?.code == 200) {
            navigate(`${publicPath}/product_manage`);
          } else {
            message.error(res.msg);
          }
        } catch (errorInfo: any) {
          console.error(errorInfo);
        }
      },
    });
  };

  const validateName = (_: any, value: any) => {
    if (value && productNameReg.test(value)) {
      return api.existNameProductManage({ name: value, id }).then((resp: any) => {
        if (resp.code === 200) {
          if (resp.data?.exist) {
            // const repeatMsg: any = staging.repeat_msg;
            // const errMsg = repeatMsg[resp.data?.typeKey] + name_label;
            return Promise.reject(product_manage.name_already);
          }
        } else {
          return Promise.reject(resp.msg);
        }
      });
    } else {
      return Promise.resolve();
    }
  };
  const layout = {
    labelCol: { span: 2 },
    wrapperCol: { span: 16 },
    // itemMarginBottom: 16,
  };
  const getSuperAuth = async () => {
    const res: any = await api.getSuperAuth();
    if (res?.code == 200) {
      const { superAccessToken } = res.data;
      token.current = superAccessToken;
    }
  };
  // 图片上传前处理
  const beforeUpload: any = async (file: RcFile, { width, height, size, accept = ["png"] }: any) => {
    await getSuperAuth();
    return new Promise<void>((resolve, reject) => {
      const isAccept = accept.some((item: string) => file.type === `image/${item}`);
      const acceptText = accept.join("、").toUpperCase();

      if (!isAccept) {
        message.error(
          `${release.upload_accept_err.text1}${width}${asterisk}${height}${release.upload_accept_err.text2}${acceptText}${release.upload_accept_err.text3}`
        );
        reject();
        return;
      }
      if (size) {
        const imgSize = file.size;
        if (imgSize > size) {
          const sizeText = changeSize(size);
          message.error(`${release.upload_size_err.text1}${sizeText}${release.upload_size_err.text2}`);
          reject();
          return;
        }
      }
      const _URL = window.URL || window.webkitURL;
      const img = new Image();
      img.src = _URL.createObjectURL(file);

      img.onerror = () => {
        message.error(release.upload_fail);
      };
      img.onload = () => {
        if (img.width != width || img.height != height) {
          message.error(
            `${release.upload_accept_err.text1}${width}${asterisk}${height}${release.upload_accept_err.text2}${acceptText}${release.upload_accept_err.text3}`
          );
          reject();
        } else {
          resolve();
        }
      };
    });
  };

  // 图标新增
  const iconImageChange: UploadProps["onChange"] = (info: UploadChangeParam<UploadFile>) => {
    if (info.file.status === "uploading") {
      setIconLoading(true);
      return;
    } else {
      setIconLoading(false);
    }
    if (info.file.status === "done") {
      if (info.file.response.status === 200) {
        const iconUrl = info?.file?.response?.result;
        setIconLoading(false);
        setIconImageUrl(iconUrl);
        baseForm.setFieldValue("photoUrl", iconUrl);
      } else {
        message.error(info.file.response?.message);
      }
    } else {
      if (!iconImageUrl?.length) {
        baseForm.setFieldValue("photoUrl", "");
        setIconImageUrl("");
      }
    }
    baseForm.validateFields(["photoUrl"]);
  };

  const handleSave = async () => {
    if (iconLoading) {
      message.warning(release.upload_img_ing);
      return;
    }

    try {
      setSaveLoading(true)
      const baseValues = await baseForm.validateFields();
      api
        .saveProductManage({
          ...baseValues,
          id: productManageDetail?.id,
        })
        .then((res: any) => {
          setSaveLoading(false)
          if (res?.code == 200) {
            message.success(save_success_text);
            getProductManageDetail(id, undefined, () => {
              setIsTampered?.(true);
            });
          } else {
            message.error(res.msg);
          }
        });
    } catch (e) {
      setSaveLoading(false)
      console.log(e);
    }
  };
  useEffect(() => {
    if (!isCurrentDetail) {
      baseForm.resetFields();
      setIconImageUrl("");
      setProductManageMapper(undefined);
      return;
    }
    baseForm.setFieldsValue({
      name: productManageDetail?.name,
      photoUrl: productManageDetail?.photoUrl,
      description: productManageDetail?.description,
    });
    setIconImageUrl(productManageDetail?.photoUrl);

    const firstTwo = productManageDetail?.histories?.slice(0, 2);
    const isDel = firstTwo?.every((item: any) => {
      const resShowMode = findDeep(release.show_mode_arr, item.showMode);
      return resShowMode?.baseBtns?.includes("delete");
    });

    const resDataCopy = {
      ...productManageDetail,
      createTimeText: format(new Date(Number(productManageDetail?.createTime)), "yyyy年MM月dd日 HH:mm:ss"),
      updateTimeText: format(new Date(Number(productManageDetail?.updateTime)), "yyyy年MM月dd日 HH:mm:ss"),
      isDel,
      disabled: productManageDetail.appBelong !== "PERSONAL",
    };
    setProductManageMapper(resDataCopy);
  }, [productManageDetail, id]);

  return (
    <div className={prefix}>
      <div className={`${prefix}-inner`}>
        <div className={`${prefix}-header`}>
          <div className={`${prefix}-header-text`}>
            <div className={`${prefix}-header-title`}>{product_detail.detail_title_text}</div>
            <div className={`${prefix}-header-tip`}>{product_detail.info_tip}</div>
          </div>
          <div className={`${prefix}-header-btns`}>
            <Flex gap="small" wrap>
              <Button
                className={`${prefix}-content-delete`}
                disabled={isTampered || productManageMapper?.disabled || !productManageDetail?.enableDeleteFlag}
                onClick={handleDelete}
              >
                {delete_text}
              </Button>
              <Button
                type="primary"
                className={`${prefix}-content-delete`}
                disabled={isTampered || productManageMapper?.disabled}
                loading={saveLoading}
                onClick={handleSave}
              >
                {save_text}
              </Button>
            </Flex>
          </div>
        </div>

        <div className={`${prefix}-content`}>
          <div className={`${prefix}-content-title`}>{product_detail.developer_title}</div>
          <div className={`${prefix}-content-developer`}>
            <Tooltip
              placement="bottomLeft"
              title={product_detail.developer_tip}
              overlayClassName={`${prefix}-content-developer-tooltip`}
              color="#fff"
              overlayInnerStyle={{
                color: "rgba(0,0,0,0.8)",
                width: 200,
                padding: "10px 16px",
                transform: "translate(-12px, 0)",
              }}
            >
              <QuestionCircleOutlined style={{ color: "#0A59F7", cursor: "pointer", fontSize: 14 }} />
            </Tooltip>
            <div className={`${prefix}-content-developer-id`}>
              <span>{product_detail.developer_ID}</span>
              {productManageMapper?.developerId}
            </div>
          </div>
          <Divider />
          <div className={`${prefix}-content-title mt-title`}>{product_detail.product}</div>
          <Form form={baseForm} {...layout} scrollToFirstError={true} className={`${prefix}-baseForm ${prefix}-form`}>
            <Form.Item label={release.icon_label} name="photoUrl" className={`${prefix}-baseForm-iconResources`}>
              <div>
                <Upload
                  accept="image/*"
                  listType="picture-card"
                  showUploadList={false}
                  disabled={isTampered || iconLoading}
                  maxCount={1}
                  action={api.uploadSuperUrl}
                  headers={{
                    "x-access-token": token.current,
                  }}
                  data={{
                    appId: id,
                    type: "ICON",
                    subType: "ICON",
                  }}
                  beforeUpload={async (e) => {
                    await beforeUpload(e, { width: 256, height: 256, accept: ["png"] });
                  }}
                  onChange={iconImageChange}
                  style={{ margin: 0 }}
                >
                  {iconImageUrl ? (
                    <img src={iconImageUrl} alt="avatar" />
                  ) : iconLoading ? (
                    <LoadingOutlined />
                  ) : (
                    <PlusSquareFilled className={`${prefix}-add-icon`} />
                  )}
                </Upload>
                <div className={`${prefix}-form-tip`}>{release.icon_tip}</div>
              </div>
            </Form.Item>

            <Form.Item
              label={product_detail.product_name}
              name="name"
              validateDebounce={500}
              getValueFromEvent={(e) => e.target.value.replace(/(^\s*)|(\s*$)/g, "")}
              rules={[
                { required: true, message: addFormJson.input_placeholder + addFormJson.product_name },
                {
                  pattern: productNameReg,
                  message: product_manage.name_validator,
                },
                { validator: validateName },
              ]}
            >
              <Input style={{ width: "30%" }} maxLength={64} showCount disabled={isTampered}></Input>
            </Form.Item>
            <Form.Item label={product_detail.product_id}>{productManageMapper?.id}</Form.Item>
            <Form.Item label={product_detail.product_type}>{productManageMapper?.deviceType?.text}</Form.Item>
            <Form.Item label={product_detail.access_gateway}>{productManageMapper?.accessName}</Form.Item>
            <Form.Item label={product_detail.product_model}>{productManageMapper?.model}</Form.Item>
            <Form.Item label={product_detail.operating_system}>{productManageMapper?.operatingSystem?.text}</Form.Item>
            <Form.Item label={product_detail.create_time}>{productManageMapper?.createTimeText}</Form.Item>
            <Form.Item label={product_detail.update_time}>{productManageMapper?.updateTimeText}</Form.Item>
            <Form.Item
              label={product_detail.product_describe}
              name="description"
              getValueFromEvent={(e) => e.target.value.replace(/(^\s*)|(\s*$)/g, "")}
            >
              <Input.TextArea
                showCount
                maxLength={200}
                style={{ width: "80%" }}
                placeholder={addFormJson.input_placeholder + addFormJson.product_describe}
                disabled={isTampered || productManageMapper?.disabled}
              />
            </Form.Item>
          </Form>
        </div>
      </div>
    </div>
  );
}

export default Info;
